import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import ShareCard from "../../common/ShareCard";
import useFansUnitedSDK from "../../hooks/useFansUnitedSDK";
import {
	getFantasyError,
	getFantasyLoading,
	getFantasyMatches,
	getFantasyMatchesRequest,
	getFantasyTemplate,
	getFantasyTemplateRequest,
} from "../../redux/fantasy";
import styles from "./UpcomingMatches.module.css";

const UpcomingMatches = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const fansUnitedSDK = useFansUnitedSDK();

	// Get data from Redux
	const template = useSelector(getFantasyTemplate);
	const matches = useSelector(getFantasyMatches);
	const isLoading = useSelector(getFantasyLoading);
	const error = useSelector(getFantasyError);

	// Local state to track active group
	const [activeGroup, setActiveGroup] = useState(null);
	const [groupTitle, setGroupTitle] = useState("");

	// Fetch the template if it's not already available
	useEffect(() => {
		if (fansUnitedSDK && !template) {
			dispatch(getFantasyTemplateRequest(fansUnitedSDK));
		}
	}, [fansUnitedSDK, dispatch, template]);

	// Then, when template is available, determine active/upcoming group
	useEffect(() => {
		if (template && template.groups) {
			const currentDate = new Date();

			// Try to find active group first (where current date is between from and to)
			let foundGroup = template.groups.find((group) => {
				const fromDate = new Date(group.filters.fromDate);
				const toDate = new Date(group.filters.toDate);
				const isActive = currentDate >= fromDate && currentDate <= toDate;
				return isActive;
			});

			// If no active group, find the next upcoming group
			if (!foundGroup) {
				const upcomingGroups = template.groups
					.filter((group) => {
						const isUpcoming = new Date(group.filters.fromDate) > currentDate;
						return isUpcoming;
					})
					.sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));

				foundGroup = upcomingGroups[0];
			}

			// If still no group found, use the first group
			if (!foundGroup && template.groups.length > 0) {
				foundGroup = template.groups[0];
			}

			if (foundGroup) {
				setActiveGroup(foundGroup.groupId);
				setGroupTitle(foundGroup.title || "");

				// Fetch matches for this group
				dispatch(getFantasyMatchesRequest(fansUnitedSDK, foundGroup.groupId));
			} else {
				console.warn("No groups found in template!");
			}
		}
	}, [template, fansUnitedSDK, dispatch]);

	// Format time for display (date and time)
	const formatMatchTime = (dateString) => {
		const date = new Date(dateString);
		// Get day and month
		const day = date.getDate().toString().padStart(2, "0");
		const month = (date.getMonth() + 1).toString().padStart(2, "0");
		// Get time in 24h format
		const hours = date.getHours().toString().padStart(2, "0");
		const minutes = date.getMinutes().toString().padStart(2, "0");

		return `${day}/${month} ${hours}:${minutes}`;
	};

	const renderMatches = () => {
		if (isLoading) {
			return <Loader />;
		}

		if (error) {
			console.error("Error rendering matches:", error);
			return <p>Error loading matches: {error}</p>;
		}

		if (!matches || matches.length === 0) {
			return <p>No upcoming matches found</p>;
		}

		// Sort matches by date
		const sortedMatches = [...matches].sort((a, b) => new Date(a.kickoffAt) - new Date(b.kickoffAt));

		// Filter to show only upcoming matches
		const upcomingMatches = sortedMatches.filter((match) => {
			const isUpcoming = new Date(match.kickoffAt) > new Date();
			return isUpcoming;
		});

		if (upcomingMatches.length === 0) {
			return <p>No upcoming matches in this round</p>;
		}

		return (
			<div className={styles.matchList}>
				{upcomingMatches.map((match) => (
					<div key={match.id} className={styles.matchItem}>
						<div className={styles.matchDate}>{formatMatchTime(match.kickoffAt)}</div>
						<div className={styles.matchTeams}>
							<div className={styles.teamContainer}>
								<img src={match.homeTeam.assets?.logo} alt={match.homeTeam.name} className={styles.teamLogo} />
								<div className={styles.teamName}>{t(match.homeTeam.name)}</div>
							</div>
						</div>
						<div className={styles.matchTeams}>
							<div className={styles.teamContainer}>
								<img src={match.awayTeam.assets?.logo} alt={match.awayTeam.name} className={styles.teamLogo} />
								<div className={styles.teamName}>{t(match.awayTeam.name)}</div>
							</div>
						</div>
					</div>
				))}
			</div>
		);
	};

	// Add a fallback if loading takes too long
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (isLoading) {
				console.log("Loading timeout reached. Current state:", { template, matches, isLoading, error });
			}
		}, 5000); // 5 seconds timeout

		return () => clearTimeout(timeoutId);
	}, [isLoading, template, matches, error]);

	return (
		<div className={styles.container}>
			<div className={styles.card}>
				<h2 className={styles.sectionTitle}>{groupTitle ? `${groupTitle} - Upcoming matches` : "Upcoming matches"}</h2>
				<div className={styles.divider}></div>
				<div className={styles.cardContent}>{renderMatches()}</div>
			</div>
			<div style={{ marginBottom: "30px" }}>
				<ShareCard />
			</div>
		</div>
	);
};

export default UpcomingMatches;
